import React from "react";

import { find, stubTrue } from "lodash/fp";
import {
  Navigate,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { Container, Typography } from "@mui/material";

import ActionBar from "@app/analytics/components/ActionBar";
import FreeTrialUpsellBanner from "@app/analytics/components/FreeTrialUpsellBanner";
import type { ForYouPage } from "@app/analytics/Pulse/types";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import useUserLocalStorage from "@app/common/hooks/useLocalStorage";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import Link from "@rui/Link";
import Stack, { Row } from "@rui/Stack";
import { StickyContainer } from "@rui/styles";
import Tab from "@rui/Tab";
import Tabs from "@rui/Tabs";

import { TabsContainer } from "./components/styles";
import ForYou from "./ForYou";
import ForYourTeams from "./ForYourTeams/ForYourTeams";
import ForYourWorkspace from "./ForYourWorkspace";
import ForYouTags from "./ForYouTags";
import PulseDateSelector from "./PulseDateSelector";
import PulseUpsell from "./PulseUpsell";
import PlanChip from "../components/PlanChip";
import useBillingContext from "../hooks/billing/useBillingContext";

interface PulseTab {
  label: string;
  path: string;
  value: ForYouPage;
  enabled: (inWorkspace: boolean) => boolean;
}

const TABS: PulseTab[] = [
  {
    label: "For You",
    path: ROUTES.FOR_YOU,
    value: "forYou",
    enabled: stubTrue,
  },
  {
    label: "Your Tags",
    path: ROUTES.FOR_YOU_TAGS,
    value: "forYouTags",
    enabled: stubTrue,
  },
  {
    label: "Your Teams",
    path: ROUTES.FOR_YOU_TEAMS,
    value: "forYourTeams",
    enabled: (inWorkspace) => inWorkspace,
  },
  {
    label: "Your Workspace",
    path: ROUTES.FOR_YOU_WORKSPACE,
    value: "forYourWorkspace",
    enabled: (inWorkspace) => inWorkspace,
  },
];

const Pulse = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { inWorkspace } = useWorkspace();
  const { currentPlan, isTrial } = useBillingContext();
  const { canAccessForYouPage } = useFeatureAccess();

  const [showTrialBanner, setShowTrialBanner] = useUserLocalStorage(
    "showForYouTrialBanner",
    true
  );

  if (!canAccessForYouPage) {
    return <PulseUpsell currentPlan={currentPlan} />;
  }

  const shouldShowBanner = isTrial && showTrialBanner;
  const dismissBanner = () => {
    setShowTrialBanner(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, path: string) => {
    navigate(path);
  };

  const selectedPage = (find({ path: location.pathname }, TABS) ?? TABS[0])
    .value;

  const enabledTabs = TABS.filter((t) => t.enabled(inWorkspace));

  return (
    <Stack sx={{ bgcolor: "#FFF", flex: 1 }}>
      <StickyContainer>
        {shouldShowBanner && (
          <FreeTrialUpsellBanner onDismiss={dismissBanner}>
            For You is a premium feature available to{" "}
            <Link to={LINKS.MARKETING.PLANS_PRICING}>
              Read Pro and Enterprise
            </Link>{" "}
            users.
          </FreeTrialUpsellBanner>
        )}
        <ActionBar sx={{ borderBottom: 0 }}>
          <Row variant="left" spacing={1}>
            <Typography variant="h2">For You</Typography>
            {isTrial && <PlanChip label="Pro+" />}
          </Row>
          <PulseDateSelector page={selectedPage} />
        </ActionBar>
        <TabsContainer>
          <Tabs value={location.pathname} onChange={handleTabChange}>
            {enabledTabs.map((tab) => (
              <Tab key={tab.path} value={tab.path} label={tab.label} />
            ))}
          </Tabs>
        </TabsContainer>
      </StickyContainer>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 4, flex: 1 }}>
        <Routes>
          <Route index element={<ForYou />} />
          <Route path="tags" element={<ForYouTags />} />
          {inWorkspace && (
            <>
              <Route path="workspace" element={<ForYourWorkspace />} />
              <Route path="teams" element={<ForYourTeams />} />
            </>
          )}
          <Route path="*" element={<Navigate to={ROUTES.FOR_YOU} />} />
        </Routes>
      </Container>
    </Stack>
  );
};

export default Pulse;
